import React from 'react'
import { Provider } from 'react-redux'
import { CartProvider } from 'use-shopping-cart'
import { ThirdwebProvider } from 'thirdweb/react'
import './src/styles/globals.css'
import { LanguageProvider } from './src/i18n/LanguageProvider'
import LanguageSuggestion from './src/components/LanguageSuggestion'
import { store } from './src/store/store'
import { rehydrate } from './src/store/authSlice'
import SessionBridge from './src/components/auth/SessionBridge'
import { trackEvent } from './src/utils/analytics'
import { captureReferralFromLocation } from './src/utils/referral'
import { captureCampaignFromLocation } from './src/utils/campaign'

// Client-side redirect: www.tworobots.com → tworobots.com
const RedirectWrapper = ({ children }) => {
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const host = window.location.host.toLowerCase()
      if (host.startsWith('www.tworobots.com')) {
        const newUrl =
          'https://tworobots.com' +
          window.location.pathname +
          window.location.search +
          window.location.hash
        window.location.replace(newUrl)
      }
    }
  }, [])

  return children
}

// Rehydrate auth from localStorage on load, and keep it in sync across tabs so
// logging in/out in one tab reflects everywhere.
const AuthRehydrator = () => {
  React.useEffect(() => {
    store.dispatch(rehydrate())
    const onStorage = e => {
      if (['jwtToken', 'refreshToken', 'user'].includes(e.key)) {
        store.dispatch(rehydrate())
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  return null
}

// Client-side only wrapper to prevent SSR issues
const ClientOnlyCartProvider = ({ children }) => {
  const [isClient, setIsClient] = React.useState(false)

  React.useEffect(() => {
    setIsClient(true)
  }, [])

  if (!isClient) {
    return children
  }

  return (
    <CartProvider
      mode="payment"
      cartMode="client-only"
      stripe={process.env.GATSBY_STRIPE_PUBLIC_KEY}
      successUrl={`${process.env.GATSBY_SITE_URL}/success`}
      cancelUrl={`${process.env.GATSBY_SITE_URL}/cart`}
      currency="USD"
      allowedCountries={['US', 'GB', 'CA']}
      billingAddressCollection={true}
      shouldPersist={true}
    >
      {children}
    </CartProvider>
  )
}

export const wrapRootElement = ({ element }) => {
  return (
    <RedirectWrapper>
      <Provider store={store}>
        <ThirdwebProvider>
          <AuthRehydrator />
          <SessionBridge />
          <LanguageProvider>
            <ClientOnlyCartProvider>{element}</ClientOnlyCartProvider>
            {/* First-visit "switch to your language?" prompt (site-wide, once). */}
            <LanguageSuggestion />
          </LanguageProvider>
        </ThirdwebProvider>
      </Provider>
    </RedirectWrapper>
  )
}

// ---------------------------------------------------------------------------
// Analytics — send key interactions to GA4 via trackEvent() (gtag).
// gatsby-plugin-google-gtag handles page views; this only adds the custom
// click events below. Everything here is read-only and wrapped so it can
// never interfere with navigation or break a click.
// ---------------------------------------------------------------------------

// Page views (initial load + every client-side route change) are sent by
// gatsby-plugin-google-gtag itself, so we don't fire them here.

// Build a short human label for an element from its aria-label, text, or title.
function labelFor(el) {
  const text =
    el.getAttribute('aria-label') ||
    (el.textContent || '').replace(/\s+/g, ' ').trim() ||
    el.getAttribute('title') ||
    ''
  return text ? text.slice(0, 100) : undefined
}

// A single delegated click listener covers the whole site, so we never have to
// wire onClick into individual components:
//   - any [data-analytics-event] -> that named event (e.g. windows_client_click)
//   - Steam store links          -> 'steam_click'
//   - the site logo (.logoCont)  -> 'logo_click'
//   - any other external link    -> 'outbound_click'
//   - any other button/control   -> 'button_click' (labelled by its text)
// Internal link navigations are already captured as page_view, so they are
// intentionally left out here.
function handleGlobalClick(e) {
  try {
    const target = e.target
    if (!target || !target.closest) return

    // 1. Explicit named events win (the download buttons use these).
    const tagged = target.closest('[data-analytics-event]')
    if (tagged) {
      trackEvent(tagged.getAttribute('data-analytics-event'), {
        label: tagged.getAttribute('data-analytics-label') || labelFor(tagged)
      })
      return
    }

    // 2. Links: name the important ones, flag any other outbound link.
    const link = target.closest('a[href]')
    if (link) {
      let url
      try {
        url = new URL(link.href, window.location.href)
      } catch (_) {
        return
      }
      const text = labelFor(link)
      if (url.href.includes('store.steampowered.com')) {
        trackEvent('steam_click', { link_url: url.href, link_text: text })
      } else if (link.closest('.logoCont, .desktop-logo-text')) {
        trackEvent('logo_click', { link_url: url.href })
      } else if (url.host && url.host !== window.location.host) {
        trackEvent('outbound_click', {
          link_url: url.href,
          link_domain: url.host,
          link_text: text
        })
      }
      return
    }

    // 3. Any other interactive control -> a generic, labelled click so we still
    //    capture the activity (Sign In, menu toggles, buy buttons, etc.).
    const control = target.closest(
      'button, [role="button"], input[type="submit"], input[type="button"]'
    )
    if (control) {
      const label = labelFor(control)
      if (label) trackEvent('button_click', { label })
    }
  } catch (_) {
    // Analytics must never break a user interaction.
  }
}

export const onClientEntry = () => {
  if (typeof document === 'undefined') return
  // Capture a creator referral code from the landing URL (?ref=…), first-touch.
  captureReferralFromLocation()
  // And a campaign code (?campaign=…, e.g. the beta invite email), kept apart.
  captureCampaignFromLocation()
  // Capture phase so we still see clicks that stop propagation, and so we run
  // before Gatsby navigates away on internal links.
  document.addEventListener('click', handleGlobalClick, true)
}

// Re-check for a ?ref= or ?campaign= on client-side navigations (first-touch
// still wins for ?ref=).
export const onRouteUpdate = ({ location }) => {
  captureReferralFromLocation(location && location.search)
  captureCampaignFromLocation(location && location.search)
}
